import { useContext, useEffect, useMemo, useState } from "react";
import { AboutFeatures } from "./features/about-features";
import { AboutGallery } from "./gallery/about-gallery";
import { AboutHero } from "./hero/about-hero";
import { AboutStats } from "./stats/about-stats";
import { AboutVideo } from "./video/about-video";
import { AboutService } from "../../services/aboutService";
import { LanguageContext } from "../../providers/LanguageProvider";
import { LANGUAGES } from "../../utils/constants/languages";
import type {AboutHeroData, AboutVideoData } from "../../interfaces/about";
import { LoaderSpinner } from "../commons/loader-spinner/loader-spinner";
import { AboutTimeline } from "./timeline/about-timeline";
import { DATE_FORMAT } from "../../utils/dateFormat";
import { SEO } from "../commons/seo/seo";

export function About(){
    const lang = useContext(LanguageContext);
    const [rawAboutData, setRawAboutData] = useState<any|null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let isMounted = true;

        setLoading(true);
        AboutService().getAboutDatas().then((elem:any) => {
            if (isMounted) {
                setRawAboutData(elem);
            }
        })
        .catch((err:any)=>console.error(err))
        .finally(()=>{
           
        })

        return () => {
            isMounted = false;
        };
    }, []);


    const aboutDatas = useMemo(() => {
        if (!rawAboutData) return null;

        const localLanguage =
            LANGUAGES.find((language) => language.code === lang.language)?.locale ??
            lang.language;
            //aboutheroes 
            let hero:AboutHeroData={};
            const {hero_title,hero_subtitle,hero_image,intro_content,intro_title,hero_video,...datas} = rawAboutData;


            if (localLanguage==='fr-FR'){
                hero = {
                    hero_title: hero_title,
                    hero_subtitle: hero_subtitle,
                    hero_image: hero_image,
                    hero_video: hero_video,
                    intro_content: intro_content,
                    intro_title: intro_title 
                }    
    
            }else{

                const traductionselected = datas.translations.find((elt:any)=>elt.languages_code===localLanguage);

                hero = {
                    hero_title: traductionselected.hero_title,
                    hero_subtitle: traductionselected.hero_subtitle,
                    hero_image: traductionselected.hero_image,
                    hero_video: hero_video,
                    intro_content: traductionselected.intro_content,
                    intro_title: traductionselected.intro_title 
                }    
            }


            //aboutstats
            const stats = rawAboutData?.stats.map(
            (elem:any)=>{
                if (localLanguage=='fr-FR'){
                    return {key:elem.key, value:elem.value};
                }else{
                    const traductionselected = elem?.translations?.find((elt:any)=>elt.languages_code== localLanguage);
                    return  {...traductionselected,value:elem.value};
                }
            }).map((newEl:any)=>({
                key:newEl.key,
                value:newEl.value
            }));

       
            //aboutfeature
            let feature:any = null;
            if(rawAboutData.features){
                if (localLanguage=='fr-FR'){
                    feature = {
                        title:rawAboutData.features[0].title,
                        description:rawAboutData.features[0].description,
                        image:rawAboutData.features[0].image,
                    };
    
                }else{
                    feature = rawAboutData?.features.map(
                        (elem:any)=>{
                            const traductionselected =  elem.translations.find((elt:any)=>elt.languages_code== localLanguage);
                            return  {
                                description:traductionselected.description,
                                image:elem.image,
                    
                            }
                    }).find((newEl:any)=>(newEl)) ;
                }
            }

            //about gallery
            let gallery:any = null;

            if(rawAboutData.gallery){

                if (localLanguage=='fr-FR'){
        
                    gallery = {
                        caption:rawAboutData.gallery[0].caption,
                        images:rawAboutData.gallery[0].images,
                    };

                }else{
                    gallery = rawAboutData?.gallery.map(
                        (elem:any)=>{
                            const traductionselected =  elem.translations.find((elt:any)=>elt.languages_code=== localLanguage);
                            return  {
                                caption:traductionselected.caption,
                                images:elem.images
                            }
                    }).find((newEl:any)=>(newEl));
                }
            }

            //about video
            let video:AboutVideoData = {};


            if(rawAboutData.video){
                if (localLanguage=='fr-FR'){
                    video = {
                        url:rawAboutData.video[0].url,
                        thumbnail:rawAboutData.video[0].thumbnail,
                        description:rawAboutData.video[0].description,
                    };
                }else{
                    video = rawAboutData?.video.map(
                        (elem:any)=>{
                            const traductionselected =  elem.translations.find((elt:any)=>elt.languages_code=== localLanguage);
                            return  {
                                description:traductionselected.description,
                                thumbnail:elem.thumbnail,
                                url:elem.url
                            }
                    }).find((newEl:any)=>(newEl));
                }
            }


            //about timeline



                let timeline = rawAboutData?.timeline.map(
                (elem:any)=>{
                    if (localLanguage=='fr-FR'){
                        return {
                            title:elem.title,
                            description:elem.description,
                            image:elem.image,
                            date: DATE_FORMAT.format(new Date(elem.date),localLanguage,'M-Y')
                        };
                    }else{
                        const traductionselected = elem.translations.find((elt:any)=>elt.languages_code== localLanguage);
                        return  {...traductionselected,
                            image:elem.image,
                            date: DATE_FORMAT.format(new Date(elem.date),localLanguage,'M-Y')
                        };
                    }
                }).map((newEl:any)=>({
                    title:newEl.title,
                    description:newEl.description,
                    date:newEl.date,
                    image:newEl.image
                }))
                timeline = [...timeline].sort(
                    (a, b)=> a.date.localeCompare(b.date)
                  );



                const result:any = {
                    ...hero,
                    stats:stats,
                    feature:feature,
                    gallery:gallery,
                    timeline:timeline,
                    video:video 
                }
                setLoading(false)   
                return result;    
                 
    }, [lang,rawAboutData]);

    return(
        <div className="page-container about-container">
            <SEO
    title="À propos — Les Toiles de la Juine"
    description="Découvrez Les Toiles de la Juine, une exposition artistique consacrée à la redécouverte du patrimoine médiéval."
/>
                            {loading ? (
                            <LoaderSpinner visible={true} />
                            ) : (
            <div>
            <AboutHero
                hero_title={aboutDatas?.hero_title}
                hero_subtitle={aboutDatas?.hero_subtitle}
                hero_image={aboutDatas?.hero_image}
                hero_video={aboutDatas?.hero_video}
            />
            <AboutStats items={aboutDatas?.stats ?? []} />
            <AboutTimeline items={aboutDatas?.timeline ?? []}/>
            <AboutFeatures item={aboutDatas?.feature ?? {}} />
            <AboutGallery images={aboutDatas?.gallery.images ?? []} caption={aboutDatas?.gallery.caption}/>
            <AboutVideo item={aboutDatas?.video ?? []} />
            </div>)}
        </div>
    );
}